<template>
  <v-container>
    <v-layout row>
      <v-flex xs12 sm8 offset-sm2>

        <!-- Loading -->
        <v-progress-circular v-if="loading" indeterminate color="primary" class="mt-5"></v-progress-circular>

        <!-- No pieces registered -->
        <div v-else-if="dancerLoaded && pieces.length === 0" class="competition-page">
          <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>
          <h1>Competition Registration</h1>
          <div class="empty-state">
            <v-icon size="48" color="#bbb">mdi-music-note-off</v-icon>
            <h3>Register a showcase piece first</h3>
            <p>You need to have at least one registered showcase piece before you can enter the competition.</p>
            <router-link to="/pieces" class="sq-btn sq-btn-primary">
              Register a Piece
            </router-link>
          </div>
        </div>

        <!-- Already registered -->
        <div v-else-if="dancerLoaded && dancer.competitionRegistered && !surveyEditing" class="competition-page">
          <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>
          <h1>Competition Registration</h1>
          <div class="registered-banner">
            <v-icon color="#2e7d32" size="28">mdi-checkbox-marked-circle</v-icon>
            <span>You're registered to compete!</span>
          </div>

          <div class="registered-details">
            <h3>Your Competition Piece</h3>
            <div class="piece-card" v-if="competitionPiece">
              <div class="piece-card-title">{{ competitionPiece.title }} — {{ competitionPiece.songArtist }}</div>
              <div class="piece-card-meta">{{ competitionPiece.pieceType }} &middot; {{ competitionPiece.groupSize }}</div>
              <div class="piece-card-division" v-if="competitionPiece.competition && competitionPiece.competition !== 'None'">
                <strong>Assigned Division:</strong> {{ competitionPiece.competition }}
              </div>
              <div class="piece-card-division pending" v-else>
                Division assignment pending.
              </div>
            </div>

            <div v-if="needsSurveyUpdate" class="survey-missing-banner">
              <strong>Your self-assessment survey needs to be updated.</strong>
              Please redo it so the IABF team can assign your division.
              <v-btn color="primary" small class="mt-2" @click="enterSurveyEdit">Complete survey</v-btn>
            </div>
            <div v-else class="survey-update-row">
              <v-btn small outlined @click="enterSurveyEdit">Update my survey responses</v-btn>
            </div>

            <div v-if="canChangeCompetitionPiece && pieceOptions.length > 1" class="change-piece-block">
              <h3>Change competition piece</h3>
              <p class="change-piece-lede">
                You can change which piece competes until final divisions are set closer to IABF.
                Changing will <strong>clear your self-assessment</strong> — you’ll fill out a new form for the new piece.
              </p>
              <button
                type="button"
                class="iabf-btn iabf-btn--secondary"
                @click="openChangePieceModal"
              >
                Change competition piece
              </button>
            </div>
            <p v-else-if="!canChangeCompetitionPiece" class="change-piece-closed">
              Competition piece changes are closed (final divisions are being set).
              Email <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> if you need help.
            </p>
            <p v-if="formError" class="error-text">{{ formError }}</p>
          </div>

          <!-- Change piece confirm modal -->
          <div
            v-if="changePieceModalOpen"
            class="comp-change-modal-root"
            @keydown.esc.prevent="closeChangePieceModal"
          >
            <div class="comp-change-modal-backdrop" @click="closeChangePieceModal" />
            <div
              class="comp-change-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="comp-change-title"
              @click.stop
            >
              <header class="comp-change-modal-header">
                <h2 id="comp-change-title">Change competition piece?</h2>
                <button
                  type="button"
                  class="comp-change-modal-close"
                  aria-label="Close"
                  :disabled="changingPiece"
                  @click="closeChangePieceModal"
                >×</button>
              </header>
              <p class="comp-change-modal-lede">
                You can change your competition piece until final divisions are set closer to IABF.
                <strong>This will clear your self-assessment answers</strong> — you’ll need to fill out
                a new form for the new piece. Are you sure?
              </p>
              <div class="comp-change-modal-body">
                <label class="form-label" for="comp-change-select">New competition piece</label>
                <select
                  id="comp-change-select"
                  v-model="selectedPieceId"
                  class="comp-change-select"
                >
                  <option
                    v-for="opt in pieceOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.text }}{{ opt.value === dancer.competitionPieceId ? ' (current)' : '' }}
                  </option>
                </select>
                <p v-if="formError" class="error-text">{{ formError }}</p>
              </div>
              <footer class="comp-change-modal-footer">
                <button
                  type="button"
                  class="iabf-btn iabf-btn--secondary"
                  :disabled="changingPiece"
                  @click="closeChangePieceModal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="iabf-btn iabf-btn--primary"
                  :disabled="
                    changingPiece ||
                    !selectedPieceId ||
                    selectedPieceId === dancer.competitionPieceId
                  "
                  @click="onChangePiece"
                >
                  {{ changingPiece ? 'Updating…' : 'Clear survey & change piece' }}
                </button>
              </footer>
            </div>
          </div>

          <!-- Competition Info -->
          <div class="competition-info">
            <h2>Competition Information</h2>
            <competition-about />
          </div>
        </div>

        <!-- Registration form (new registration OR existing dancer updating their survey) -->
        <div v-else-if="dancerLoaded" class="competition-page">
          <router-link v-if="!surveyEditing" to="/pieces" class="back-link">&larr; Back to Showcase pieces</router-link>
          <a v-else href="#" class="back-link" @click.prevent="cancelSurveyEdit">&larr; Back to Competition</a>
          <h1>{{ surveyEditing ? 'Update Your Survey' : 'Competition Registration' }}</h1>

          <!-- Competition Info (collapsed by default — decision + form come first) -->
          <details v-if="!surveyEditing" class="competition-info-details">
            <summary>About the IABF competition</summary>
            <competition-about />
          </details>

          <h2 v-if="!surveyEditing">Register to Compete</h2>

          <div v-if="!surveyEditing && registrationClosed" class="iabf-banner deadline-notice">
            <strong>Competition registration is now closed.</strong>
            If you'd still like to compete, email <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>. We'll do our best to fit you in.
          </div>

          <template v-if="surveyEditing || !registrationClosed">
          <p v-if="!surveyEditing">Fill out the form below to enter one of your showcase pieces into the competition. The IABF team will review your responses and assign your division.</p>
          <p v-else>Your self-assessment helps the IABF team pick the right division for you. Update any answers below and save.</p>

          <form @submit.prevent="surveyEditing ? onSurveyUpdate() : onSubmit()" class="competition-form">

            <!-- Q1: Which piece (only when registering, not when updating an existing survey) -->
            <div v-if="!surveyEditing" class="form-group">
              <label class="form-label">1. Which piece would you like to enter for competition?</label>
              <v-select
                v-model="selectedPieceId"
                :items="pieceOptions"
                item-text="text"
                item-value="value"
                label="Select a piece"
                outlined
                :rules="[v => !!v || 'Please select a piece']"
              ></v-select>
            </div>

            <!-- Q2: Style -->
            <div class="form-group">
              <label class="form-label">2. What style is your competition piece?</label>
              <v-radio-group v-model="survey.style">
                <v-radio label="Classical Variation" value="Classical Variation"></v-radio>
                <v-radio label="My own Ballet Choreography" value="My own Ballet Choreography"></v-radio>
                <v-radio label="My own Contemporary/Lyrical" value="My own Contemporary/Lyrical"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q3: Shoe type -->
            <div class="form-group">
              <label class="form-label">3. What type of shoes will you be wearing?</label>
              <v-radio-group v-model="survey.shoeType">
                <v-radio label="En Pointe" value="En Pointe"></v-radio>
                <v-radio label="Flat Shoes/Barefoot" value="Flat Shoes/Barefoot"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q4: Performance format -->
            <div class="form-group">
              <label class="form-label">4. What is the format of your performance?</label>
              <v-radio-group v-model="survey.performanceFormat">
                <v-radio label="Solo" value="Solo"></v-radio>
                <v-radio label="Pas de Deux/Duet" value="Pas de Deux/Duet"></v-radio>
                <v-radio label="Group 3-10" value="Group 3-10"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q5: Pointe training duration (conditional) -->
            <div class="form-group" v-if="survey.shoeType === 'En Pointe'">
              <label class="form-label">5. How long have you been training en pointe?</label>
              <v-radio-group v-model="survey.pointeTrainingDuration">
                <v-radio label="Less than 1 year" value="Less than 1 year"></v-radio>
                <v-radio label="1-3 years" value="1-3 years"></v-radio>
                <v-radio label="3+ years" value="3+ years"></v-radio>
                <v-radio label="Professional" value="Professional"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q6: Ballet journey stage -->
            <div class="form-group">
              <label class="form-label">{{ survey.shoeType === 'En Pointe' ? '6' : '5' }}. Which best describes where you are in your ballet journey?</label>
              <v-radio-group v-model="survey.balletJourneyStage">
                <v-radio label="I'm a beginner — I've been dancing for a few years and I'm still learning foundational steps" value="Beginner — still learning foundational steps"></v-radio>
                <v-radio label="I'm intermediate — I'm comfortable with most ballet vocabulary and can perform combinations with some complexity" value="Intermediate — comfortable with most ballet vocabulary"></v-radio>
                <v-radio label="I'm advanced — I've trained extensively and can execute complex combinations with confidence" value="Advanced — trained extensively, complex combinations with confidence"></v-radio>
                <v-radio label="I'm a professional or pre-professional dancer" value="Professional or pre-professional"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q7: Performance experience -->
            <div class="form-group">
              <label class="form-label">{{ questionNumber(7) }}. How would you describe your performance experience?</label>
              <v-radio-group v-model="survey.performanceExperience">
                <v-radio label="This will be my first time performing on stage" value="First time performing on stage"></v-radio>
                <v-radio label="I've performed in a few recitals or showcases" value="A few recitals or showcases"></v-radio>
                <v-radio label="I perform regularly in showcases, competitions, or community productions" value="Regular showcases, competitions, or community productions"></v-radio>
                <v-radio label="I've performed at IABF before" value="Performed at IABF before"></v-radio>
                <v-radio label="I have professional or semi-professional performance experience" value="Professional or semi-professional experience"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q8: Balance and control -->
            <div class="form-group">
              <label class="form-label">{{ questionNumber(8) }}. How would you describe your balance and control?</label>
              <v-radio-group v-model="survey.balanceAndControl">
                <v-radio label="I'm still working on holding balances — I wobble often and need the barre for stability" value="Still working on holding balances, need barre for stability"></v-radio>
                <v-radio label="I can hold balances briefly in the center and I'm gaining confidence away from the barre" value="Can hold balances briefly in center, gaining confidence"></v-radio>
                <v-radio label="I can balance confidently in relevé and hold positions in the center with control" value="Balance confidently in relevé, hold positions with control"></v-radio>
                <v-radio label="I have strong, reliable balance and can sustain balances in complex positions" value="Strong reliable balance, sustain in complex positions"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q9: Turning ability -->
            <div class="form-group">
              <label class="form-label">{{ questionNumber(9) }}. How would you describe your turning ability?</label>
              <v-radio-group v-model="survey.turningAbility">
                <v-radio label="I'm still learning how to spot and complete a single pirouette" value="Still learning to spot and complete single pirouette"></v-radio>
                <v-radio label="I can do a clean single pirouette most of the time" value="Clean single pirouette most of the time"></v-radio>
                <v-radio label="I can consistently do clean double pirouettes" value="Consistently clean double pirouettes"></v-radio>
                <v-radio label="I can do triples or more with control" value="Triples or more with control"></v-radio>
                <v-radio label="I'm comfortable with a variety of turns (fouettés, à la seconde, etc.)" value="Comfortable with variety of turns"></v-radio>
                <v-radio label="Turning is not part of my piece" value="Turning is not part of my piece"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q10: Footwork experience -->
            <div class="form-group">
              <label class="form-label">{{ questionNumber(10) }}. How would you describe your footwork experience?</label>
              <v-radio-group v-model="survey.footworkExperience">
                <v-radio label="I'm still building coordination with petit allégro and quick transitions" value="Still building coordination with petit allégro"></v-radio>
                <v-radio label="I can manage basic petit allégro (changements, echappés, glissades) at a moderate tempo" value="Basic petit allégro at moderate tempo"></v-radio>
                <v-radio label="I'm comfortable with a range of petit allégro and can handle faster tempos" value="Comfortable with range of petit allégro, faster tempos"></v-radio>
                <v-radio label="I can execute intricate footwork (entrechats, brisés, quick batterie) with clarity" value="Intricate footwork with clarity"></v-radio>
                <v-radio label="Quick footwork is not part of my piece" value="Quick footwork is not part of my piece"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q11: Musicality & expression -->
            <div class="form-group">
              <label class="form-label">{{ questionNumber(11) }}. How would you describe your musicality and expression?</label>
              <v-radio-group v-model="survey.musicalityExpression">
                <v-radio label="I focus mostly on getting the steps right — expression and musicality are still developing" value="Focus mostly on getting steps right"></v-radio>
                <v-radio label="I'm beginning to connect movement with music and expression, but it's not always consistent" value="Beginning to connect movement with music, not always consistent"></v-radio>
                <v-radio label="I dance musically and expressively, and I bring emotion and character to my performances" value="Dance musically and expressively with emotion and character"></v-radio>
                <v-radio label="Artistry and musicality are central to my dancing — I use dynamics, phrasing, and expression intentionally" value="Artistry and musicality central, use dynamics and phrasing intentionally"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q12: Combination execution -->
            <div class="form-group">
              <label class="form-label">{{ questionNumber(12) }}. How comfortably could you execute the following combination: Balancé, Tombé Pas de Bourrée, Chassé into 4th, Pirouette?</label>
              <v-radio-group v-model="survey.combinationExecution">
                <v-radio label="I wouldn't be able to do this combination yet" value="Wouldn't be able to do this yet"></v-radio>
                <v-radio label="I could attempt it slowly with some guidance" value="Could attempt slowly with some guidance"></v-radio>
                <v-radio label="I could do it at a moderate tempo with a few rough spots" value="Moderate tempo with a few rough spots"></v-radio>
                <v-radio label="I could do it smoothly and musically at tempo" value="Smoothly and musically at tempo"></v-radio>
                <v-radio label="I could do this with ease and add artistic flair" value="With ease and artistic flair"></v-radio>
              </v-radio-group>
            </div>

            <!-- Q13: Challenging movements -->
            <div class="form-group">
              <label class="form-label">{{ questionNumber(13) }}. What movements in your piece feel most challenging for you right now?</label>
              <v-textarea
                v-model="survey.challengingMoments"
                outlined
                rows="3"
                placeholder="Describe the most challenging parts of your piece..."
              ></v-textarea>
            </div>

            <!-- Q14: Joyful movements -->
            <div class="form-group">
              <label class="form-label">{{ questionNumber(14) }}. What movements in your piece bring you the most joy?</label>
              <v-textarea
                v-model="survey.joyfulMoments"
                outlined
                rows="3"
                placeholder="Describe the movements you love most..."
              ></v-textarea>
            </div>

            <!-- Q15: Artistic risks -->
            <div class="form-group">
              <label class="form-label">{{ questionNumber(15) }}. Are there any artistic or technical risks you're taking in your piece that you're proud of?</label>
              <v-textarea
                v-model="survey.artisticRisks"
                outlined
                rows="3"
                placeholder="Tell us about any risks or challenges you're embracing..."
              ></v-textarea>
            </div>

            <!-- Q16: Performance goal -->
            <div class="form-group">
              <label class="form-label">{{ questionNumber(16) }}. What is one thing you hope to gain from the competition experience?</label>
              <v-textarea
                v-model="survey.performanceGoal"
                outlined
                rows="3"
                placeholder="What do you hope to take away from competing..."
              ></v-textarea>
            </div>

            <!-- Only show when history has golds — no empty “none on file” state -->
            <div
              v-if="!historyLoading && priorGoldMedals.length"
              class="form-group prior-gold-block"
            >
              <label class="form-label">Previous IABF gold medals</label>
              <div class="prior-gold-card">
                <p class="prior-gold-lede">
                  From your festival history — if you won gold at Beginner or Intermediate in a
                  division, please enter the next level for that same style this year.
                </p>
                <ul class="prior-gold-list">
                  <li v-for="(g, i) in priorGoldMedals" :key="'gold-' + i">
                    <strong>{{ g.year }}</strong>
                    <span v-if="g.division"> — {{ g.division }}</span>
                    <span v-else-if="g.pieceTitle"> — {{ g.pieceTitle }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <p v-if="formError" class="error-text">{{ formError }}</p>

            <v-btn
              type="submit"
              color="primary"
              dark
              class="submit-btn"
              :disabled="submitting"
            >
              {{ submitting ? 'Saving...' : (surveyEditing ? 'Save Survey' : 'Submit Competition Registration') }}
            </v-btn>
            <v-btn v-if="surveyEditing" text class="ml-2" :disabled="submitting" @click="cancelSurveyEdit">Cancel</v-btn>
          </form>
          </template>
        </div>

      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import CompetitionAbout from '@/components/iabf/CompetitionAbout.vue'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'
import {
  isCompetitionPieceChangeClosed,
  isCompetitionRegistrationClosed
} from '@/config/iabf-registration-2027'
import {
  formatPriorGoldSummary,
  priorGoldMedalsFromYearPanels
} from '@/utils/iabf-festival-history'

export default {
  components: {
    CompetitionAbout
  },
  data () {
    return {
      selectedPieceId: '',
      submitting: false,
      changingPiece: false,
      changePieceModalOpen: false,
      surveyEditing: false,
      formError: '',
      dancerLoaded: false,
      historyLoading: false,
      historyYearPanels: [],
      survey: {
        style: '',
        shoeType: '',
        performanceFormat: '',
        pointeTrainingDuration: '',
        balletJourneyStage: '',
        performanceExperience: '',
        balanceAndControl: '',
        turningAbility: '',
        footworkExperience: '',
        musicalityExpression: '',
        combinationExecution: '',
        challengingMoments: '',
        joyfulMoments: '',
        artisticRisks: '',
        performanceGoal: '',
        // Filled from competition history on submit (not asked of the dancer)
        previousGold: ''
      }
    }
  },
  computed: {
    loading () {
      return this.$store.getters.loading
    },
    dancer () {
      return this.$store.getters.currentIABFDancer || {}
    },
    person () {
      return this.$store.getters.currentIABFPerson || {}
    },
    user () {
      return this.$store.getters.user
    },
    pieces () {
      return this.$store.getters.loadedIABFPiecesforDancer || []
    },
    pieceOptions () {
      return (this.pieces || [])
        .filter(p => (p.approvalStatus || 'approved') !== 'pending')
        .map(p => ({
          text: `${p.title} — ${p.songArtist} (${p.pieceType})`,
          value: p.id
        }))
    },
    dancerId () {
      return this.dancer.id || null
    },
    competitionPiece () {
      if (!this.dancer.competitionPieceId) return null
      return this.pieces.find(p => p.id === this.dancer.competitionPieceId) || null
    },
    festivalYear () {
      return Number(this.$store.getters.iabfDancerYear) ||
        Number(this.dancer && this.dancer.festivalYear) ||
        IABF_DANCER_YEAR
    },
    registrationClosed () {
      return isCompetitionRegistrationClosed(this.festivalYear)
    },
    canChangeCompetitionPiece () {
      return !isCompetitionPieceChangeClosed(this.festivalYear)
    },
    priorGoldMedals () {
      return priorGoldMedalsFromYearPanels(this.historyYearPanels)
    },
    needsSurveyUpdate () {
      // Admin cleared the survey but the dancer is still registered — she
      // needs to redo it for the new/changed piece.
      if (!this.dancer || !this.dancer.competitionRegistered) return false
      const s = this.dancer.competitionSurvey
      if (!s) return true
      // Treat a survey with no meaningful answers as missing too.
      return Object.values(s).every(v => v === '' || v == null)
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    if (this.dancerId) {
      await this.$store.dispatch('loadIABFPiecesForDancer', this.dancerId)
      this.preselectCompetitionPiece()
      this.dancerLoaded = true
      this.loadCompetitionHistory()
    }
  },
  watch: {
    'dancer.id' (val) {
      if (val && !this.dancerLoaded) {
        this.$store.dispatch('loadIABFPiecesForDancer', val).then(() => {
          this.preselectCompetitionPiece()
          this.dancerLoaded = true
          this.loadCompetitionHistory()
        })
      } else if (val) {
        this.loadCompetitionHistory()
      }
    }
  },
  methods: {
    /**
     * Prefer ?piece= from the Showcase hub, then the dancer's saved competition piece.
     */
    preselectCompetitionPiece () {
      const fromQuery = this.$route && this.$route.query && this.$route.query.piece
      const pieceIds = new Set((this.pieces || []).map(p => p.id))
      if (fromQuery && pieceIds.has(fromQuery)) {
        this.selectedPieceId = fromQuery
        return
      }
      if (this.dancer.competitionPieceId) {
        this.selectedPieceId = this.dancer.competitionPieceId
      }
    },
    async loadCompetitionHistory () {
      if (!this.dancer.id && !(this.user && this.user.email)) {
        return
      }
      this.historyLoading = true
      try {
        const result = await this.$store.dispatch('loadDancerFestivalHistory', {
          email: this.dancer.email || (this.user && this.user.email),
          firstName: this.dancer.firstName,
          lastName: this.dancer.lastName,
          yearsAttended: this.person.yearsAttended || this.dancer.yearsAttended,
          enrollmentYear: this.dancer.festivalYear || this.$store.getters.iabfDancerYear,
          personId: this.dancer.personId || (this.person && this.person.id)
        })
        this.historyYearPanels = (result && result.years) || []
        this.survey.previousGold = formatPriorGoldSummary(this.priorGoldMedals)
      } catch (e) {
        console.warn('Competition history load failed', e)
        this.historyYearPanels = []
      } finally {
        this.historyLoading = false
      }
    },
    /** Survey payload with previousGold always derived from history when available. */
    surveyWithHistoryGolds () {
      const previousGold = this.priorGoldMedals.length
        ? formatPriorGoldSummary(this.priorGoldMedals)
        : (this.survey.previousGold || '')
      return { ...this.survey, previousGold }
    },
    questionNumber (base) {
      // If en pointe, question 5 is the pointe duration question, so all subsequent questions shift by 0
      // If not en pointe, the pointe question is hidden, so numbers shift down by 1
      if (this.survey.shoeType === 'En Pointe') {
        return base
      }
      return base - 1
    },
    validateForm () {
      if (!this.selectedPieceId) return 'Please select a piece to enter.'
      if (!this.survey.style) return 'Please select the style of your piece.'
      if (!this.survey.shoeType) return 'Please select your shoe type.'
      if (!this.survey.performanceFormat) return 'Please select your performance format.'
      if (this.survey.shoeType === 'En Pointe' && !this.survey.pointeTrainingDuration) {
        return 'Please select how long you have been training en pointe.'
      }
      if (!this.survey.balletJourneyStage) return 'Please select where you are in your ballet journey.'
      if (!this.survey.performanceExperience) return 'Please describe your performance experience.'
      if (!this.survey.balanceAndControl) return 'Please describe your balance and control.'
      if (!this.survey.turningAbility) return 'Please describe your turning ability.'
      if (!this.survey.footworkExperience) return 'Please describe your footwork experience.'
      if (!this.survey.musicalityExpression) return 'Please describe your musicality and expression.'
      if (!this.survey.combinationExecution) return 'Please answer the combination execution question.'
      return null
    },
    async onSubmit () {
      this.formError = ''
      const error = this.validateForm()
      if (error) {
        this.formError = error
        this.$nextTick(() => {
          const el = this.$el && this.$el.querySelector('.error-text')
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
        return
      }
      this.submitting = true
      try {
        await this.$store.dispatch('submitCompetitionRegistration', {
          dancerId: this.dancerId,
          pieceId: this.selectedPieceId,
          survey: this.surveyWithHistoryGolds()
        })
        // Reload dancer to get updated state
        await this.$store.dispatch('loadIABFDancer', this.dancerId)
      } catch (e) {
        this.formError = 'Something went wrong. Please try again.'
      }
      this.submitting = false
    },
    enterSurveyEdit () {
      // Pre-fill from existing answers so a partial update doesn't wipe
      // unrelated questions.
      const existing = this.dancer.competitionSurvey || {}
      for (const key of Object.keys(this.survey)) {
        if (existing[key] !== undefined) this.survey[key] = existing[key]
      }
      // Prefer live history over a stale free-text answer
      if (this.priorGoldMedals.length) {
        this.survey.previousGold = formatPriorGoldSummary(this.priorGoldMedals)
      }
      this.formError = ''
      this.surveyEditing = true
      // Jump back to the top of the page so the form is visible.
      this.$nextTick(() => { window.scrollTo({ top: 0, behavior: 'smooth' }) })
      this.loadCompetitionHistory()
    },
    cancelSurveyEdit () {
      this.surveyEditing = false
      this.formError = ''
    },
    async onSurveyUpdate () {
      this.formError = ''
      const err = this.validateSurveyOnly()
      if (err) { this.formError = err; return }
      this.submitting = true
      try {
        await this.$store.dispatch('updateCompetitionSurvey', {
          dancerId: this.dancerId,
          survey: this.surveyWithHistoryGolds()
        })
        await this.$store.dispatch('loadIABFDancer', this.dancerId)
        this.surveyEditing = false
      } catch (e) {
        this.formError = 'Something went wrong. Please try again.'
      }
      this.submitting = false
    },
    validateSurveyOnly () {
      // Same checks as validateForm but skips the piece-picker.
      if (!this.survey.style) return 'Please select the style of your piece.'
      if (!this.survey.shoeType) return 'Please select your shoe type.'
      if (!this.survey.performanceFormat) return 'Please select your performance format.'
      if (this.survey.shoeType === 'En Pointe' && !this.survey.pointeTrainingDuration) {
        return 'Please select how long you have been training en pointe.'
      }
      if (!this.survey.balletJourneyStage) return 'Please select where you are in your ballet journey.'
      if (!this.survey.performanceExperience) return 'Please describe your performance experience.'
      if (!this.survey.balanceAndControl) return 'Please describe your balance and control.'
      if (!this.survey.turningAbility) return 'Please describe your turning ability.'
      if (!this.survey.footworkExperience) return 'Please describe your footwork experience.'
      if (!this.survey.musicalityExpression) return 'Please describe your musicality and expression.'
      if (!this.survey.combinationExecution) return 'Please answer the combination execution question.'
      return ''
    },
    openChangePieceModal () {
      if (!this.canChangeCompetitionPiece) {
        this.formError = 'Competition piece changes are closed. Email hello@iabf.dance if you need help.'
        return
      }
      this.formError = ''
      this.selectedPieceId = this.dancer.competitionPieceId || ''
      this.changePieceModalOpen = true
      this.$nextTick(() => {
        document.body.style.overflow = 'hidden'
      })
    },
    closeChangePieceModal () {
      if (this.changingPiece) {
        return
      }
      this.changePieceModalOpen = false
      this.formError = ''
      document.body.style.overflow = ''
    },
    async onChangePiece () {
      this.formError = ''
      if (!this.canChangeCompetitionPiece) {
        this.formError = 'Competition piece changes are closed. Email hello@iabf.dance if you need help.'
        return
      }
      if (!this.selectedPieceId || this.selectedPieceId === this.dancer.competitionPieceId) {
        this.formError = 'Choose a different piece, or cancel.'
        return
      }
      this.changingPiece = true
      try {
        await this.$store.dispatch('updateCompetitionPiece', {
          dancerId: this.dancerId,
          pieceId: this.selectedPieceId
        })
        await this.$store.dispatch('loadIABFDancer', this.dancerId)
        this.changePieceModalOpen = false
        document.body.style.overflow = ''
        // Survey cleared — open the re-assessment form
        this.enterSurveyEdit()
      } catch (e) {
        this.formError = 'Could not update your competition piece. Please try again.'
      }
      this.changingPiece = false
    }
  }
}
</script>

<style scoped>
.competition-page {
  max-width: 780px;
  margin: 0 auto;
  padding: 40px 20px 60px;
  color: var(--iabf-ink, #1c1b1f);
}

.change-piece-block {
  margin-top: 24px;
}
.change-piece-lede {
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 14px;
}
.change-piece-closed {
  margin-top: 20px;
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.45;
}
.change-piece-closed a {
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
}

.comp-change-modal-root {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 12px;
  overflow-y: auto;
}
.comp-change-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(28, 27, 31, 0.45);
}
.comp-change-modal {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 480px;
  margin: 12px auto 40px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 12px 48px rgba(28, 27, 31, 0.18);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  display: flex;
  flex-direction: column;
}
.comp-change-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 0;
}
.comp-change-modal-header h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 22px;
  margin: 0;
  border: none;
  padding: 0;
  color: var(--iabf-ink, #1c1b1f);
}
.comp-change-modal-close {
  border: none;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: var(--iabf-ink-faint, #8a8792);
  cursor: pointer;
  padding: 0 4px;
  margin: 0;
}
.comp-change-modal-lede {
  margin: 10px 20px 0;
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
}
.comp-change-modal-body {
  padding: 14px 20px 8px;
}
.comp-change-select {
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: 48px;
  margin-top: 6px;
  padding: 10px 12px;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  font-size: 16px;
  background: #fff;
  color: var(--iabf-ink, #1c1b1f);
}
.comp-change-modal-footer {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 20px 18px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
}
.iabf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  text-decoration: none;
}
.iabf-btn--primary {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}
.iabf-btn--secondary {
  background: #fff;
  color: var(--iabf-primary, #8a957d);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
}
.iabf-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.competition-info-details {
  margin: 16px 0 28px;
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 14px;
  padding: 14px 18px;
}
.competition-info-details summary {
  cursor: pointer;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 18px;
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  user-select: none;
}
.competition-info-details summary::-webkit-details-marker {
  display: none;
}
.competition-info-details summary::after {
  content: '▾';
  font-size: 14px;
  line-height: 1;
  color: var(--iabf-primary, #8a957d);
  transition: transform 0.15s ease;
  flex-shrink: 0;
}
.competition-info-details[open] summary {
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}
.competition-info-details[open] summary::after {
  transform: rotate(180deg);
}

.survey-missing-banner {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 18px 20px;
  margin: 16px 0 24px;
  font-family: var(--iabf-font-body, inherit);
  color: var(--iabf-ink, #1c1b1f);
}
.survey-missing-banner strong {
  display: block;
  margin-bottom: 6px;
  color: var(--iabf-ink, #1c1b1f);
  font-weight: 700;
}
.survey-update-row {
  margin: 8px 0 24px;
}

.deadline-notice {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 20px 22px;
  margin: 16px 0;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  line-height: 1.5;
  color: var(--iabf-ink, #1c1b1f);
}
.deadline-notice strong {
  display: block;
  margin-bottom: 6px;
  color: var(--iabf-ink, #1c1b1f);
  font-weight: 700;
}
.deadline-notice a { color: var(--iabf-primary, #8a957d); }

.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
  transition: color 0.15s;
}

.back-link:hover {
  color: var(--iabf-ink, #1c1b1f);
}

h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -.02em;
  line-height: 1.3;
  color: var(--iabf-ink, #1c1b1f);
  margin: 16px 0 8px 0;
}

h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 28px;
  letter-spacing: -.01em;
  line-height: 1.3;
  color: var(--iabf-ink, #1c1b1f);
  margin: 40px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}

h3 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 20px;
  color: var(--iabf-ink, #1c1b1f);
  margin: 28px 0 10px 0;
}

h4 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 17px;
  color: var(--iabf-ink, #1c1b1f);
  margin: 20px 0 6px 0;
}

p, li {
  font-family: var(--iabf-font-body, inherit);
  font-weight: 400;
  font-size: 17px;
  line-height: 1.8;
  color: var(--iabf-ink-muted, #5c5a63);
}

ul {
  padding-left: 20px;
  margin: 4px 0 12px 0;
}

li {
  margin-bottom: 4px;
}

a {
  color: var(--iabf-ink, #1c1b1f);
  text-decoration: underline;
}

.error-text {
  color: var(--iabf-danger, #b54a4a);
  margin-bottom: 12px;
}

.small-note {
  font-size: 15px;
  font-style: italic;
  color: var(--iabf-ink-faint, #8a8792);
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  border: 1px dashed #ddd;
  border-radius: 8px;
  margin-top: 30px;
}

.empty-state h3 {
  margin: 16px 0 8px 0;
}

.empty-state p {
  color: var(--iabf-ink-faint, #8a8792);
  margin-bottom: 20px;
}

/* Registered banner */
.registered-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #e8f5e9;
  border-radius: 8px;
  margin: 20px 0;
}

.registered-banner span {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 20px;
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
}

/* Piece card */
.piece-card {
  background: #fafaf8;
  border: 1px solid var(--iabf-border, #e3e0e8);
  border-radius: 6px;
  padding: 18px 20px;
  margin: 12px 0 24px 0;
}

.piece-card-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 18px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
}

.piece-card-meta {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 4px 0;
}

.piece-card-division {
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
}

.piece-card-division.pending {
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
}

/* Form */
.competition-form {
  margin-top: 20px;
}

.form-group {
  margin-bottom: 8px;
}

.form-label {
  display: block;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 17px;
  color: var(--iabf-ink, #1c1b1f);
  margin-bottom: 8px;
}

.prior-gold-block {
  margin-top: 8px;
  margin-bottom: 16px;
}

.prior-gold-card {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink, #1c1b1f);
}

.prior-gold-lede {
  margin: 0 0 10px;
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
}

.prior-gold-list {
  margin: 0;
  padding-left: 1.15em;
}

.prior-gold-list li {
  margin-bottom: 4px;
}

.submit-btn {
  margin-top: 16px;
  margin-bottom: 40px;
}

/* Buttons */
.sq-btn {
  display: inline-block;
  padding: 12px 24px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  letter-spacing: .05em;
  text-decoration: none;
  border: 1px solid var(--iabf-primary, #8a957d);
  color: var(--iabf-primary, #8a957d);
  background: transparent;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.sq-btn:hover {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}

.sq-btn-primary {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}

.sq-btn-primary:hover {
  background: #333;
  color: #fff;
}

.competition-info {
  margin-top: 20px;
}

.registered-details {
  margin-top: 10px;
}
</style>
